import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Typography } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import IosShareIcon from '@mui/icons-material/IosShare';
import CloseIcon from '@mui/icons-material/Close';

import { NAV_BG, AMBER, AMBER_DARK } from '../theme';
const DISMISS_KEY = 'sb_pwa_install_dismissed';

const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export default function PWAInstallBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  useEffect(() => {
    if (!isMobile() || isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    setIsIOSDevice(isIOS());

    if (!isIOS()) {
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setVisible(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    } else {
      // iOS: show manual instructions banner
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    const onShow = () => {
      if (!isStandalone()) {
        setIsIOSDevice(isIOS());
        setVisible(true);
      }
    };
    window.addEventListener('pwa-install-show', onShow);
    return () => window.removeEventListener('pwa-install-show', onShow);
  }, []);

  if (!visible) return null;

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setVisible(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
    window.dispatchEvent(new CustomEvent('pwa-install-dismissed'));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        background: `linear-gradient(90deg, ${NAV_BG} 0%, #163571 100%)`,
        borderBottom: `2px solid ${AMBER}`,
      }}
    >
      {isIOSDevice
        ? <IosShareIcon sx={{ color: AMBER, fontSize: 20, flexShrink: 0 }} />
        : <GetAppIcon sx={{ color: AMBER, fontSize: 20, flexShrink: 0 }} />
      }
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#e2e8f0', flex: 1 }}>
        {isIOSDevice ? t('pwa_install.ios_message') : t('pwa_install.message')}
      </Typography>
      {!isIOSDevice && (
        <Button
          size="small"
          variant="contained"
          disableElevation
          onClick={handleInstall}
          sx={{
            background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
            color: NAV_BG,
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 1.5,
            fontSize: '0.8rem',
            px: 1.5,
            py: 0.4,
            flexShrink: 0,
          }}
        >
          {t('pwa_install.install')}
        </Button>
      )}
      <IconButton size="small" onClick={handleDismiss} sx={{ color: '#94a3b8', p: 0.5, flexShrink: 0 }}>
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}
